import React from "react";
import { UseFormReturn } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { EditCompanyFormData } from "../../schemas/companySchemas";

interface Trade {
  id: number;
  name: string;
  description: string;
}

interface TradesSectionProps {
  form: UseFormReturn<EditCompanyFormData>;
  tradeList: Trade[];
}

export const TradesSection: React.FC<TradesSectionProps> = ({
  form,
  tradeList,
}) => {
  const { control, watch, setValue } = form;
  const selectedTradeIds = watch("tradeIds") || [];

  const handleTradeToggle = (tradeId: number, checked: boolean) => {
    const currentIds = selectedTradeIds || [];
    if (checked) {
      setValue("tradeIds", [...currentIds, tradeId]);
    } else {
      setValue("tradeIds", currentIds.filter((id: number) => id !== tradeId));
    }
  };

  const handleSelectAll = () => {
    setValue("tradeIds", tradeList.map(trade => trade.id));
  };

  const handleSelectNone = () => {
    setValue("tradeIds", []);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <FormDescription>
          Select which trades this company has access to
        </FormDescription>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-sm text-primary hover:underline"
          >
            Select All
          </button>
          <span className="text-muted-foreground">|</span>
          <button
            type="button"
            onClick={handleSelectNone}
            className="text-sm text-primary hover:underline"
          >
            Clear All
          </button>
        </div>
      </div>

      <FormField
        control={control}
        name="tradeIds"
        render={() => (
          <FormItem>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tradeList.map((trade) => (
                <FormItem
                  key={trade.id}
                  className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border p-4"
                >
                  <FormControl>
                    <Checkbox
                      checked={selectedTradeIds.includes(trade.id)}
                      onCheckedChange={(checked) =>
                        handleTradeToggle(trade.id, checked as boolean)
                      }
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-medium">
                      {trade.name}
                    </FormLabel>
                    <FormDescription className="text-xs text-muted-foreground">
                      {trade.description}
                    </FormDescription>
                  </div>
                </FormItem>
              ))}
            </div>
          </FormItem>
        )}
      />

      <div className="text-sm text-muted-foreground mt-2">
        {selectedTradeIds.length} of {tradeList.length} trades selected
      </div>
    </div>
  );
};